import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Handshake, ArrowRight, Clock, User } from "lucide-react";
import { REF_STATUS_HEX, PRIORITY_HEX, pretty, hexA, fmtDate } from "./referralConstants";

const StatusChip = ({ status, testid }) => {
  const hex = REF_STATUS_HEX[status] || REF_STATUS_HEX.draft;
  return (
    <span data-testid={testid} className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: hexA(hex, 0.14), color: hex }}>{pretty(status)}</span>
  );
};

const OPEN = new Set(["sent", "viewed", "accepted", "appointment_pending", "tour_pending",
  "quote_pending", "quote_uploaded", "in_progress", "follow_up_needed"]);

export default function ExternalReferrals() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("open");

  useEffect(() => {
    api.get("/external/referrals").then(({ data }) => setData(data)).catch(() => setData({ items: [], open_count: 0 }));
  }, []);

  if (!data) return <div className="space-y-3"><Skeleton className="h-12" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;

  const items = data.items || [];
  const open = items.filter((r) => OPEN.has(r.status));
  const closed = items.filter((r) => !OPEN.has(r.status));
  const shown = tab === "open" ? open : tab === "closed" ? closed : items;

  return (
    <div data-testid="external-referrals-page" className="space-y-5">
      <div className="flex items-center gap-2">
        <Handshake size={22} className="text-[#1C4358]" />
        <h1 className="font-display text-2xl font-bold text-[#252221]">Your referrals</h1>
        <span className="text-[13px] text-muted-foreground">{items.length} total · {open.length} open</span>
      </div>

      <div className="flex gap-1.5">
        {[["open", "Open", open.length], ["closed", "Completed / Closed", closed.length], ["all", "All", items.length]].map(([k, label, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`external-referrals-tab-${k}`}
            className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              tab === k ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#E6EDF2] hover:border-[#1C4358]/40"}`}>
            {label} <span className="tabular-nums">{n}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card className="p-10 border-[#E6EDF2] text-center" data-testid="external-referrals-empty">
          <span className="mx-auto h-12 w-12 rounded-2xl bg-[#1C4358]/10 text-[#1C4358] flex items-center justify-center"><Handshake size={22} /></span>
          <p className="text-[15px] text-muted-foreground mt-3">No referrals here yet. New referrals from MovingMyMama will appear on this page.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => (
            <button key={r.id} onClick={() => nav(`/external/referrals/${r.id}`)} data-testid={`external-referral-card-${r.id}`}
              className="w-full text-left rounded-xl bg-white border border-[#E6EDF2] p-4 hover:border-[#1C4358]/40 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusChip status={r.status} testid={`external-referral-status-${r.id}`} />
                    <span className="text-[13px] font-semibold text-[#1C4358]">{pretty(r.referral_type)}</span>
                    {r.priority && r.priority !== "normal" && (
                      <span className="text-[11px] font-bold" style={{ color: PRIORITY_HEX[r.priority] }}>{pretty(r.priority)}</span>
                    )}
                  </div>
                  {r.summary && <p className="text-[14px] text-[#252221] mt-1.5 line-clamp-2">{r.summary}</p>}
                  {r.client?.name && <div className="text-[12px] text-[#4B5563] mt-1 flex items-center gap-1"><User size={12} /> {r.client.name}</div>}
                  {r.next_step && OPEN.has(r.status) && (
                    <div className="text-[12px] text-[#9C1E22] font-medium mt-1.5">Next: {r.next_step}</div>
                  )}
                </div>
                <ArrowRight size={18} className="text-[#9AA5B1] shrink-0 mt-1" />
              </div>
              <div className="text-[11px] text-[#9AA5B1] mt-2 flex items-center gap-1"><Clock size={11} /> Updated {fmtDate(r.updated_at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
